import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ChevronLeft, ChevronRight, Plus, X, Calendar, Pencil, Clock, DoorOpen,
} from 'lucide-react';
import api from '../services/api';
import BarraAgende from '../components/agenda/BarraAgende';
import AgendaSettimana from '../components/agenda/AgendaSettimana';
import SedutaPacchetto from '../components/agenda/SedutaPacchetto';
import { giornoValido } from '../components/agenda/giorno';
import { PannelloAppuntamento, PannelloNuovo } from '../components/agenda/PannelliAgenda';
import { stileNuovo } from '../navigazione/paginaIniziale';
import useRicercaPrestazioni from '../hooks/useRicercaPrestazioni';
import SegnoMancate, { PallinoMancate } from '../components/SegnoMancate';
import '../styles/Agenda.css';
import '../styles/AgendaBarra.css';
import { mescola, testoSopra } from '../utils/colori';
import { useAuthStore } from '../store/useStore';
import { puoOrganizzare } from '../utils/permessi';
import { ascolta, annunciaAgendaCambiata } from '../utils/annunci';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import useAzione from '../navigazione/useAzione';
import { notificaErrore, notificaAvviso, messaggioDa } from '../utils/notifiche';
import {
    oggiClinica, oraIta, oraDecimaleIta, giornoIta,
    istanteItaliano, perCampoItaliano, momentoItaliano,
} from '../utils/oraItaliana';
import { templateAPI } from '../services/templateApi';
import SceltaFirma from '../components/pazienti/SceltaFirma';
import FirmaConsenso from '../components/pazienti/FirmaConsenso';

/**
 * Agenda — la griglia giornaliera.
 *
 * Le colonne sono le agende che lavorano quel giorno: con quaranta
 * medici e dodici in sede, mostrarle tutte sarebbe illeggibile.
 *
 * Le sovrapposizioni si vedono affiancate, non nascoste: capita di
 * avere due pazienti insieme, e chi guarda deve accorgersene.
 */

const ORA_INIZIO = 7;      // la griglia parte dalle 07:00
const ORA_FINE = 22;
const ALTEZZA_ORA = 60;    // pixel: un'ora sta in 60px, un quarto d'ora in 15

/* «Oggi», le ore e le posizioni sulla griglia vengono tutte da
   `utils/oraItaliana.js`. Prima erano `toISOString()` (che da' UTC) e
   `getHours()` (che da' il fuso del browser): nella stessa schermata
   convivevano due fusi, e nessuno dei due era quello della clinica. */
const oggiIso = () => oggiClinica();

/* Un giorno scritto nell'indirizzo vale solo se e' una data vera: la regola
   sta in components/agenda/giorno.js (la legge anche il pannello del nuovo
   appuntamento). */

const dueCifre = (n) => String(n).padStart(2, '0');

const oraDiTesto = (t) => {
    if (!t) return 0;
    const [h, m] = String(t).split(':').map(Number);
    return h + m / 60;
};

const oraDiData = (iso) => oraDecimaleIta(iso);

const orario = (iso) => oraIta(iso);

const dataEstesa = (iso) => new Date(iso).toLocaleDateString('it-IT', {
    timeZone: 'Europe/Rome',
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
});

/**
 * Dispone gli appuntamenti sovrapposti su colonne affiancate.
 * Chi si accavalla non deve coprire l'altro.
 */
function disponi(appuntamenti) {
    const ordinati = [...appuntamenti].sort(
        (a, b) => new Date(a.inizio) - new Date(b.inizio));
    const colonne = [];   // fine dell'ultimo appuntamento per colonna

    return ordinati.map((a) => {
        const inizio = new Date(a.inizio).getTime();
        const fine = new Date(a.fine).getTime();

        let col = colonne.findIndex((f) => f <= inizio);
        if (col === -1) { col = colonne.length; colonne.push(fine); }
        else colonne[col] = fine;

        // Quanti si sovrappongono a questo: determina la larghezza
        const insieme = ordinati.filter((x) =>
            new Date(x.inizio).getTime() < fine && new Date(x.fine).getTime() > inizio);

        return { ...a, colonna: col, insieme: Math.max(insieme.length, col + 1) };
    });
}

/* Con lo stile nuovo «Console» la griglia e il pannello a destra stanno
   affiancati in .ag-divisa; con quello di prima la griglia sta dove stava e
   il pannello non c'e' (interruttore dello stile, 14/10/2026). Fuori dal
   componente perche' un componente dichiarato dentro un altro si
   rimonterebbe a ogni disegno. */
const Divisa = ({ attivo, pannello, children }) => (attivo
    ? <div className="ag-divisa"><div className="ag-vista">{children}</div>{pannello}</div>
    : <>{children}</>);

const Agenda = () => {
    /* Le rotte di orari e stanze sono di admin e segreteria: il
       collegamento segue il permesso della rotta, non il contrario, e il
       permesso ha un nome solo, in utils/permessi.js. Fino al 22 settembre
       2026 questo nome era dichiarato nel componente del dettaglio e usato
       qui: ReferenceError in render, agenda giu' per tre giorni. */
    const ruolo = useAuthStore((st) => st.user?.ruolo);
    const organizza = puoOrganizzare(ruolo);
    /* Stesso commit, stesso difetto: `navigate` esisteva solo nel
       dettaglio, e i due pulsanti qui sopra sarebbero crollati al clic. */
    const navigate = useNavigate();
    /* IL GIORNO STA NELL'INDIRIZZO (Alex, 2/10/2026): ?giorno=AAAA-MM-GG,
       come le linguette del paziente dal 28/09. Prima era solo uno stato del
       componente, e un aggiornamento della pagina - anche quello che fa da
       sola quando esce una versione nuova (utils/versioneInLinea.js) -
       riportava l'agenda a oggi mentre si guardava un altro giorno.
       Letto all'apertura, scritto a ogni cambio di giorno SOSTITUENDO
       l'indirizzo (replace), non aggiungendo un passo: dieci clic sulla
       freccia non devono chiedere dieci «Indietro» per uscire dall'agenda.
       Oggi non si scrive: l'indirizzo senza giorno vuol dire «oggi», cosi'
       un'agenda lasciata su oggi e ricaricata domattina apre il giorno
       nuovo, non quello di ieri. Il giorno e' una data, non un dato di un
       paziente. */
    const [parametri, setParametri] = useSearchParams();
    const giornoIndirizzo = parametri.get('giorno');
    const [data, setDataStato] = useState(() => giornoValido(giornoIndirizzo) || oggiIso());
    /* La casella della data, scritta a mano, passa da valori a meta' (un
       segmento cancellato, un anno di una cifra): l'agenda li prende come
       prima, e l'indirizzo segue solo un giorno vero. */
    const setData = (g) => {
        setDataStato(g);
        if (!giornoValido(g)) return;
        const scrivi = g === oggiIso() ? null : g;
        if (scrivi === giornoIndirizzo) return;
        setParametri((p) => {
            const n = new URLSearchParams(p);
            if (scrivi) n.set('giorno', scrivi); else n.delete('giorno');
            return n;
        }, { replace: true });
    };
    /* L'indirizzo cambiato da fuori: la voce «Agenda» del menu (apre oggi),
       «Indietro» e «Avanti» del browser (il giorno di quel passo). Un giorno
       che non esiste si toglie dall'indirizzo e si apre oggi. Il giorno
       appena scritto da qui coincide gia' con quello a video: niente da fare. */
    useEffect(() => {
        const valido = giornoValido(giornoIndirizzo);
        if (giornoIndirizzo !== null && !valido) {
            setParametri((p) => {
                const n = new URLSearchParams(p);
                n.delete('giorno');
                return n;
            }, { replace: true });
        }
        const atteso = valido || oggiIso();
        setDataStato((d) => (d === atteso ? d : atteso));
    }, [giornoIndirizzo]);
    const [griglia, setGriglia] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);

    const [aperto, setAperto] = useState(null);      // appuntamento in dettaglio
    const [nuovo, setNuovo] = useState(null);        // { agenda_id, inizio }
    /* LO STILE NUOVO «CONSOLE» (fase 2; interruttore dello stile, Alex
       14/10/2026): il dettaglio di un appuntamento e il nuovo appuntamento
       sono un PANNELLO A DESTRA (components/agenda/PannelliAgenda.jsx) invece
       delle finestre di sotto. Uno alla volta: aprire l'uno chiude l'altro. */
    const stileConsole = stileNuovo(useAuthStore((st) => st.user));
    /* Il pannello aperto dice qui come chiedere prima di perdere quello che
       vi si e' scritto (useUscitaProtetta): un clic su un altro appuntamento
       o su un orario cambia pannello solo se la risposta e' si'. */
    const chiediPannello = useRef(null);
    const puoCambiarePannello = () => !chiediPannello.current || chiediPannello.current();
    /* Sale quando l'agenda si rilegge per un gesto fatto dal pannello: il
       pannello si rilegge con lei. */
    const [versione, setVersione] = useState(0);
    const apriAppuntamento = (x) => {
        if (stileConsole) {
            if (aperto && aperto.id === x.id) return;
            if (!puoCambiarePannello()) return;
            setNuovo(null);
        }
        setAperto(x);
    };
    const aperture = useRef(0);
    const apriNuovo = (contesto) => {
        if (stileConsole) {
            if (!puoCambiarePannello()) return;
            setAperto(null);
            aperture.current += 1;
            setNuovo({ ...contesto, chiave: aperture.current });
            return;
        }
        setNuovo(contesto);
    };
    /* «Nuovo appuntamento» dalla barra comandi (Ctrl K) o da «Nuovo».
       Con lo stile nuovo APRE IL PANNELLO (Alex, 4/10/2026: «"Nuovo
       appuntamento" da barra/Ctrl K apre il pannello»): senza un clic sulla
       griglia l'agenda e il giorno sono PROPOSTI e si vedono e si cambiano
       nel pannello, l'ora resta da scegliere - un orario inventato sarebbe
       un appuntamento preso dove nessuno ha scelto. Le regole del salvataggio
       sono quelle della rotta, come col clic. «Appuntamento» dalla cartella di
       un paziente porta il paziente gia' scelto (dati.paziente).
       Con lo stile di prima l'agenda non ha un pulsante: il modulo nasce da
       un clic sulla griglia, che dice QUALE agenda e A CHE ORA. Inventarli
       qui - la prima colonna, il prossimo quarto d'ora - vorrebbe dire
       prenotare dove nessuno ha scelto; quindi si arriva sull'agenda e si
       dice come si fa. */
    useAzione({
        'nuovo-appuntamento': (azione, dati) => {
            if (!stileConsole) {
                notificaAvviso('Per il nuovo appuntamento fai clic sull\'orario, nella colonna dell\'agenda.');
                return;
            }
            if (!puoCambiarePannello()) return;
            setAperto(null);
            aperture.current += 1;
            setNuovo({ libero: true, giorno: giornoValido(data) || oggiIso(), chiave: aperture.current,
                       paziente: dati?.paziente || null });
        },
    });
    const [tutteAgende, setTutteAgende] = useState([]);
    const [agendeLette, setAgendeLette] = useState(false);
    /* Il medico collegato all'utente: la sua agenda e' la proposta del
       pannello aperto senza un clic sulla griglia. */
    const medicoCollegato = useAuthStore((st) => st.user?.medico_id) || null;
    const [extra, setExtra] = useState([]);          // agende aggiunte a mano
    const [nascoste, setNascoste] = useState([]);    // colonne tolte dalla vista
    const [vista, setVista] = useState('giorno');    // giorno | settimana
    const [agendaSett, setAgendaSett] = useState(null);

    const corpo = useRef(null);
    const colonne = useRef({});   // id agenda -> elemento, per sapere dove si rilascia

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            setErrore(null);
            const r = await api.get('/agenda/giorno', {
                params: { data, agende: extra.join(',') || undefined },
            });
            setGriglia(r.data.data);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile caricare l\'agenda');
        } finally {
            setCaricamento(false);
        }
    }, [data, extra]);

    useEffect(() => { carica(); }, [carica]);

    /* La griglia si rilegge quando qualcun altro cambia l'agenda.
       Oggi succede da un punto solo - il riquadro dei ritardi, che segna
       un paziente presente - ed e' esattamente il caso in cui il pallino
       giallo doveva comparire e non compariva: chi premeva «e' in sala
       d'attesa» vedeva sparire l'avviso e non succedere niente dove si
       aspettava di vederlo, cioe' sul nome in griglia. */
    useEffect(() => ascolta('agenda', carica), [carica]);

    /* L'elenco completo serve subito: la barra lo mostra tutto,
       non solo chi lavora oggi. */
    useEffect(() => {
        api.get('/agenda/agende')
            .then((r) => { setTutteAgende(r.data.data.agende || []); setAgendeLette(true); })
            .catch(() => { setTutteAgende([]); setAgendeLette(true); });
    }, []);

    /* All'apertura la griglia si posiziona sull'ora corrente, non
       alle sette del mattino. */
    useEffect(() => {
        if (!griglia || !corpo.current) return;
        const ora = Number(momentoItaliano().ora.slice(0, 2));
        if (data === oggiIso() && ora > ORA_INIZIO + 2) {
            corpo.current.scrollTop = (ora - ORA_INIZIO - 1) * ALTEZZA_ORA;
        }
    }, [griglia, data]);

    const cambiaGiorno = (giorni) => {
        /* `new Date('AAAA-MM-GG')` e' mezzanotte UTC: sommare i giorni
           li' e rileggere con toISOString torna giusto, ma solo per
           caso. Si passa dal giorno italiano, come tutto il resto. */
        const d = new Date(`${data}T12:00:00Z`);
        d.setUTCDate(d.getUTCDate() + giorni);
        setData(giornoIta(d));
    };


    /* --- righe orarie --- */
    const ore = useMemo(() => {
        const v = [];
        for (let h = ORA_INIZIO; h <= ORA_FINE; h++) v.push(h);
        return v;
    }, []);

    /* --- linea dell'ora corrente --- */
    const adesso = useMemo(() => {
        if (data !== oggiIso()) return null;
        const h = oraDecimaleIta(new Date());
        if (h < ORA_INIZIO || h > ORA_FINE) return null;
        return (h - ORA_INIZIO) * ALTEZZA_ORA;
    }, [data, griglia]);

    const fascePer = (agendaId) =>
        (griglia?.fasce || []).filter((f) => f.agenda_id === agendaId);

    const appPer = (agendaId) =>
        disponi((griglia?.appuntamenti || []).filter(
            (a) => a.agenda_id === agendaId));

    // Il colore della colonna e' quello del medico (sull'agenda), non
    // piu' quello della stanza della prima fascia: due medici nella stessa
    // stanza devono restare distinguibili. Grigio neutro se non impostato.
    const coloreDi = (agendaId) => {
        const ag = (griglia?.agende || []).find((a) => a.id === agendaId);
        return ag?.colore || '#cfd8dc';
    };


    /* --- trascinamento degli appuntamenti ---
       Si sposta solo in verticale: cambiare colonna vorrebbe dire
       cambiare medico, e per quello c'e' la finestra di modifica.
       Sotto i cinque pixel non e' un trascinamento ma un clic. */
    const [trascina, setTrascina] = useState(null);

    const iniziaTrascinamento = (e, app) => {
        if (app.stato === 'annullato') return;
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        setTrascina({ id: app.id, y0: e.clientY, dy: 0, x0: e.clientX, dx: 0 });
    };

    const muoviTrascinamento = (e) => {
        setTrascina((t) => (t
            ? { ...t, dy: e.clientY - t.y0, dx: e.clientX - t.x0 }
            : null));
    };

    const fineTrascinamento = async (e, app) => {
        e.stopPropagation();
        if (!trascina || trascina.id !== app.id) return;
        const { dy, dx } = trascina;
        const cx = e.clientX;
        setTrascina(null);

        if (Math.abs(dy) < 5 && Math.abs(dx) < 5) { apriAppuntamento(app); return; }

        // un'ora vale ALTEZZA_ORA pixel; si arrotonda al quarto d'ora
        const minuti = Math.round(dy / ALTEZZA_ORA * 60 / 15) * 15;

        /* Su quale colonna e' stato lasciato: si misurano le colonne
           vere invece di calcolarne la larghezza, cosi' funziona
           anche quando cambia il numero di agende in griglia. */
        const sotto = Object.entries(colonne.current).find(([, el]) => {
            if (!el) return false;
            const r = el.getBoundingClientRect();
            return cx >= r.left && cx <= r.right;
        });
        const agendaDest = sotto ? parseInt(sotto[0], 10) : app.agenda_id;

        if (!minuti && agendaDest === app.agenda_id) return;

        /* Cambiare colonna vuol dire cambiare medico: un errore qui
           non si nota, l'appuntamento resta in griglia solo sotto
           il nome sbagliato. Quindi si chiede. */
        if (agendaDest !== app.agenda_id) {
            const nomeDi = (id) => (griglia?.agende || [])
                .find((z) => z.id === id)?.nome || 'un\u2019altra agenda';
            const ok = window.confirm(
                `Spostare l\u2019appuntamento da ${nomeDi(app.agenda_id)} `
                + `a ${nomeDi(agendaDest)}?`
            );
            if (!ok) { carica(); return; }
        }

        const vecchioInizio = new Date(app.inizio);
        const durata = new Date(app.fine) - vecchioInizio;
        const nuovoInizio = new Date(vecchioInizio.getTime() + minuti * 60000);

        try {
            const r = await api.put(`/agenda/${app.id}`, {
                inizio: nuovoInizio.toISOString(),
                fine: new Date(nuovoInizio.getTime() + durata).toISOString(),
                agenda_id: agendaDest,
            });
            const c = r.data.data?.conflitti;
            if (c && (c.stanza?.length || c.agenda?.length)) {
                notificaAvviso(r.data.message);
            }
        } catch (err) {
            notificaErrore(messaggioDa(err, 'Non riesco a spostare l\u2019appuntamento'));
        }
        carica();
    };

    /* Chi lavora oggi arriva dal server; qui si toglie solo chi
       l'utente ha deselezionato nella barra. */
    const idsInGriglia = (griglia?.agende || []).map((a) => a.id);
    const agendeVisibili = (griglia?.agende || [])
        .filter((a) => !nascoste.includes(a.id));

    const commutaAgenda = (id, mostrare) => {
        if (mostrare) {
            setNascoste((n) => n.filter((x) => x !== id));
            if (!idsInGriglia.includes(id)) setExtra((e) => [...e, id]);
        } else if (extra.includes(id)) {
            setExtra((e) => e.filter((x) => x !== id));
        } else {
            setNascoste((n) => [...n, id]);
        }
    };

    /* DOPO «PRENOTA» SENZA IL CLIC SULLA GRIGLIA (stile nuovo): l'agenda deve
       mostrare dove e' finito l'appuntamento - il giorno, nella vista del
       giorno, con la colonna di quell'agenda (aggiunta a mano se quel giorno
       non lavora) - e scorrere sull'ora. Chi prenota lo ritrova. Dopo un clic
       sulla griglia non serve: giorno e colonna sono gia' quelli. */
    const daMostrare = useRef(null);   // { agendaId, giorno, ora, aggiunta }
    const mostraNuovo = (dove) => {
        daMostrare.current = { ...dove, aggiunta: false };
        setVista('giorno');
        setNascoste((n) => n.filter((x) => x !== dove.agendaId));
        if (dove.giorno === data) carica(); else setData(dove.giorno);
    };
    useEffect(() => {
        const m = daMostrare.current;
        if (!m || !griglia || griglia.data !== m.giorno) return;
        const inGriglia = (griglia.agende || []).some((a) => Number(a.id) === m.agendaId);
        if (!inGriglia && !m.aggiunta) {
            /* La griglia si rilegge con la colonna e si torna qui. Una volta
               sola: un'agenda chiusa per un'eccezione quel giorno il server
               non la mostra nemmeno scelta a mano, e non si gira a vuoto. */
            m.aggiunta = true;
            setExtra((e) => (e.includes(m.agendaId) ? e : [...e, m.agendaId]));
            return;
        }
        daMostrare.current = null;
        if (corpo.current) corpo.current.scrollTop = Math.max(0, (m.ora - ORA_INIZIO - 1) * ALTEZZA_ORA);
    }, [griglia]);

    /* La proposta di partenza del pannello aperto senza un clic (Alex,
       4/10/2026): l'agenda del medico collegato all'utente; altrimenti
       l'agenda in vista - quella della settimana, o la prima colonna della
       griglia. Finche' gli elenchi non sono arrivati non si propone niente, e
       il pannello la prende quando arriva (se nessuno ha gia' scelto). E'
       solo una proposta: l'agenda si vede e si cambia nel pannello, e
       l'abilitazione la decide la rotta. */
    const agendaProposta = (() => {
        if (medicoCollegato) {
            if (!agendeLette) return null;
            const sua = tutteAgende.find((a) => Number(a.medico_id) === Number(medicoCollegato));
            if (sua) return Number(sua.id);
        }
        if (vista === 'settimana') return agendaSett ? Number(agendaSett) : null;
        return agendeVisibili.length ? Number(agendeVisibili[0].id) : null;
    })();

    /* Il pannello a destra (stile nuovo): il dettaglio o il nuovo appuntamento. */
    const pannello = !stileConsole ? null : (aperto ? (
        <PannelloAppuntamento
            key={aperto.id}
            iniziale={aperto}
            versione={versione}
            colore={coloreDi(aperto.agenda_id)}
            nomeAgenda={tutteAgende.find((a) => a.id === aperto.agenda_id)?.nome}
            rifChiedi={chiediPannello}
            onChiudi={() => setAperto(null)}
            onAggiornato={() => { setVersione((v) => v + 1); carica(); annunciaAgendaCambiata(); }}
        />
    ) : (nuovo ? (
        <PannelloNuovo
            key={nuovo.chiave || 0}
            contesto={nuovo}
            agende={tutteAgende}
            proposta={agendaProposta}
            rifChiedi={chiediPannello}
            onChiudi={() => setNuovo(null)}
            onCreato={(dove) => {
                setNuovo(null);
                if (dove) mostraNuovo(dove); else carica();
            }}
        />
    ) : null));

    return (
        <div className={`ag-wrap${pannello ? ' ag-wrap--pannello' : ''}`}>
            <BarraAgende
                tutte={tutteAgende}
                idsInGriglia={idsInGriglia}
                nascoste={nascoste}
                extra={extra}
                onCommuta={commutaAgenda}
                colore={coloreDi}
            />

            {/* ---------- testata ---------- */}
            <div className="ag-testata">
                <div className="ag-navigazione">
                    <button onClick={() => setData(oggiIso())} className="ag-oggi">Oggi</button>
                    <button onClick={() => cambiaGiorno(-1)} aria-label="Giorno precedente">
                        <ChevronLeft size={18} />
                    </button>
                    <button onClick={() => cambiaGiorno(1)} aria-label="Giorno successivo">
                        <ChevronRight size={18} />
                    </button>
                    {/* Non trattiene l'aggiornamento della pagina: il giorno
                        scritto qui finisce nell'indirizzo, e la pagina
                        ricaricata lo ritrova (utils/versioneInLinea.js). */}
                    <input type="date" value={data} onChange={(e) => setData(e.target.value)}
                           data-non-trattiene />
                    <span className="ag-data">{dataEstesa(data)}</span>
                </div>

                <div className="ag-viste">
                    <button
                        className={vista === 'giorno' ? 'attiva' : ''}
                        onClick={() => setVista('giorno')}
                    >
                        Giorno
                    </button>
                    <button
                        className={vista === 'settimana' ? 'attiva' : ''}
                        onClick={() => setVista('settimana')}
                    >
                        Settimana
                    </button>
                </div>

                {/* Orari e stanze si raggiungevano SOLO dai riquadri in
                    Amministrazione. Ma gli orari di un'agenda si cercano
                    guardando l'agenda, e la stanza e' un campo
                    dell'appuntamento: e' qui che una persona si ferma e
                    pensa «adesso dovrei poterli cambiare».
                    Il riquadro in Amministrazione resta dov'era.

                    Solo a chi le rotte gia' ammettono: sono riservate ad
                    admin e segreteria, e un collegamento che porta a un
                    403 insegna a diffidare dei collegamenti. */}
                {organizza && (
                    <div className="ag-organizza">
                        <button onClick={() => navigate('/orari')}
                                title="Orari di apertura delle agende">
                            <Clock size={15} aria-hidden="true" /> Orari
                        </button>
                        <button onClick={() => navigate('/stanze')}
                                title="Stanze e apparecchiature">
                            <DoorOpen size={15} aria-hidden="true" /> Stanze
                        </button>
                    </div>
                )}
            </div>

            {errore && <div className="ag-errore">{errore}</div>}

            <Divisa attivo={stileConsole} pannello={pannello}>
            {vista === 'settimana' ? (
                <AgendaSettimana
                    agende={tutteAgende}
                    agendaId={agendaSett}
                    onCambiaAgenda={setAgendaSett}
                    dataIniziale={data}
                    onApri={apriAppuntamento}
                />
            ) : caricamento ? (
                <div className="ag-stato">Caricamento…</div>
            ) : !griglia?.agende.length ? (
                <div className="ag-stato">
                    <Calendar size={28} />
                    <p>Nessuno in sede {data === oggiIso() ? 'oggi' : 'questo giorno'}.</p>
                    <p className="ag-nota">
                        Spunta un&apos;agenda nella barra a sinistra per vederla comunque.
                    </p>
                </div>
            ) : (
                <div className="ag-griglia">
                    {/* intestazioni delle colonne */}
                    <div className="ag-intestazioni">
                        <div className="ag-angolo" />
                        {agendeVisibili.map((a) => (
                            <div key={a.id} className="ag-colonna-testa"
                                 style={{ borderTopColor: coloreDi(a.id) }}>
                                <b title={a.nome}>{a.nome}</b>
                                <span>{a.specializzazione || 'struttura'}</span>
                                {fascePer(a.id)[0]?.stanza && (
                                    <em style={{ color: coloreDi(a.id) }}>
                                        {fascePer(a.id)[0].stanza}
                                    </em>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* corpo scorrevole */}
                    <div className="ag-corpo" ref={corpo}>
                        <div className="ag-interno"
                             style={{ height: (ORA_FINE - ORA_INIZIO + 1) * ALTEZZA_ORA }}>

                            {/* righe delle ore */}
                            <div className="ag-ore">
                                {ore.map((h) => (
                                    <div key={h} className="ag-ora"
                                         style={{ height: ALTEZZA_ORA }}>
                                        <span>{dueCifre(h)}:00</span>
                                    </div>
                                ))}
                            </div>

                            {/* colonne */}
                            {agendeVisibili.map((a) => (
                                <div key={a.id} className="ag-colonna"
                                     {...(stileConsole ? { 'data-agenda': a.id } : {})}
                                     ref={(el) => { colonne.current[a.id] = el; }}>
                                    {/* righe di sfondo, un quarto d'ora ciascuna */}
                                    {ore.map((h) => [0, 15, 30, 45].map((m) => (
                                        <div key={`${h}-${m}`}
                                             className={`ag-slot ${m === 0 ? 'ora' : ''}`}
                                             style={{ top: (h - ORA_INIZIO) * ALTEZZA_ORA
                                                          + (m / 60) * ALTEZZA_ORA,
                                                      height: ALTEZZA_ORA / 4 }}
                                             onClick={() => apriNuovo({
                                                 agenda_id: a.id,
                                                 agenda_nome: a.nome,
                                                 inizio: `${data}T${dueCifre(h)}:${dueCifre(m)}`,
                                             })}
                                        />
                                    )))}

                                    {/* fasce di lavoro: sfondo chiaro */}
                                    {fascePer(a.id).map((f, i) => {
                                        const da = oraDiTesto(f.ora_inizio);
                                        const al = oraDiTesto(f.ora_fine);
                                        return (
                                            <div key={i} className="ag-fascia"
                                                 style={{
                                                     top: (da - ORA_INIZIO) * ALTEZZA_ORA,
                                                     height: (al - da) * ALTEZZA_ORA,
                                                     // Lo sfondo della fascia e' il colore del MEDICO: e'
                                                     // quello che serve per riconoscere la colonna a colpo
                                                     // d'occhio. Prima usava f.colore, cioe' la stanza, che
                                                     // quasi sempre e' vuota: da li' il grigio ovunque.
                                                     // '22' e' l'opacita' in esadecimale (~13%): tinge senza
                                                     // coprire gli appuntamenti che ci stanno sopra.
                                                     background: coloreDi(a.id) + '22',
                                                 }} />
                                        );
                                    })}

                                    {/* appuntamenti */}
                                    {appPer(a.id).map((x) => {
                                        const da = oraDiData(x.inizio);
                                        const al = oraDiData(x.fine);
                                        const largh = 100 / x.insieme;
                                        return (
                                            <button
                                                key={x.id}
                                                {...(stileConsole ? { 'data-appuntamento': x.id } : {})}
                                                className={`ag-app ag-${x.tipo} ag-stato-${x.stato}`
                                                    + (trascina && trascina.id === x.id ? ' ag-trascinato' : '')}
                                                style={{
                                                    top: (da - ORA_INIZIO) * ALTEZZA_ORA,
                                                    height: Math.max((al - da) * ALTEZZA_ORA, 18),
                                                    left: `${x.colonna * largh}%`,
                                                    width: `calc(${largh}% - 3px)`,
                                                    borderLeftColor: coloreDi(a.id),
                                                    /* Lo stesso colore della colonna, piu' marcato: la
                                                       fascia sotto e' al 13%, lo slot al 55%. Cosi' si
                                                       legge di chi e' lo slot senza risalire
                                                       all'intestazione della colonna. I blocchi no: non
                                                       sono di nessun paziente e devono restare neutri. */
                                                    ...(x.tipo === 'blocco' ? {} : {
                                                        background: mescola(coloreDi(a.id), 0.55),
                                                        color: testoSopra(mescola(coloreDi(a.id), 0.55)),
                                                    }),
                                                    transform: trascina && trascina.id === x.id
                                                        ? `translate(${trascina.dx}px, ${trascina.dy}px)`
                                                        : undefined,
                                                }}
                                                onPointerDown={(e) => iniziaTrascinamento(e, x)}
                                                onPointerMove={muoviTrascinamento}
                                                onPointerUp={(e) => fineTrascinamento(e, x)}
                                            >
                                                <span className="ag-app-ora">
                                                    {orario(x.inizio)}
                                                </span>
                                                <span className="ag-app-nome">
                                                    {/* Il pallino dice a che punto e' il paziente, e si
                                                        legge senza aprire niente. Verde vince sul giallo:
                                                        chi ha pagato e' finito, che sia arrivato e' ovvio.
                                                        Il giallo dice «arrivato», non «in sala d'attesa»:
                                                        resta giallo anche dopo «Trasforma in visita», e chi
                                                        aspetta ancora lo dice una definizione sola
                                                        (services/salaAttesa.js, il numero accanto ad
                                                        Agenda). Fino al 2/10/2026 il titolo diceva «In
                                                        sala d'attesa», cioe' una seconda definizione.
                                                        Il verde, dal 3/10/2026, e' «uscito» (Alex): la
                                                        visita incassata per intero, o chiusa dal medico
                                                        senza niente da incassare - la stessa uscita delle
                                                        analisi e della sala (services/salaAttesa.js,
                                                        USCITA_DEL_PAZIENTE). Prima era solo «pagata», e la
                                                        seduta di un pacchetto non diventava mai verde. */}
                                                    {x.uscito ? (
                                                        <i className="ag-stato-pallino ag-sp-verde"
                                                           title="Uscito: visita pagata, o chiusa senza niente da incassare" />
                                                    ) : x.presenza === 'presente' ? (
                                                        <i className="ag-stato-pallino ag-sp-giallo"
                                                           title="Arrivato" />
                                                    ) : null}
                                                    {x.tipo === 'blocco'
                                                        ? x.titolo
                                                        : (x.paz_cognome
                                                            ? `${x.paz_cognome} ${x.paz_nome}`
                                                            : x.paziente_nome)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}

                            {/* linea dell'ora corrente */}
                            {adesso != null && (
                                <div className="ag-adesso" style={{ top: adesso }} />
                            )}
                        </div>
                    </div>
                </div>
            )}
            </Divisa>

            {/* ---------- dettaglio (stile di prima: una finestra) ---------- */}
            {!stileConsole && aperto && (
                <DettaglioAppuntamento
                    appuntamento={aperto}
                    onChiudi={() => setAperto(null)}
                    onAggiornato={() => { setAperto(null); carica(); }}
                />
            )}

            {/* ---------- nuovo (stile di prima: una finestra) ---------- */}
            {!stileConsole && nuovo && (
                <NuovoAppuntamento
                    contesto={nuovo}
                    onChiudi={() => setNuovo(null)}
                    onCreato={() => { setNuovo(null); carica(); }}
                />
            )}
        </div>
    );
};

/* ============================================================
   Dettaglio
   ============================================================ */
const DettaglioAppuntamento = ({ appuntamento: x, onChiudi, onAggiornato }) => {
    const navigate = useNavigate();
    const [inCorso, setInCorso] = useState(false);
    /* Consenso obbligatorio (22 settembre 2026, migration 201): se una
       prestazione dell'appuntamento lo chiede e il paziente non l'ha
       firmato, «Trasforma in visita» verrebbe rifiutato. Qui si firma
       prima, come in «Nuova procedura». Fuori da una procedura non c'e'
       un'altra schermata dove far firmare un consenso di prestazione. */
    const [firmaPrima, setFirmaPrima] = useState(null);   // { consensi, medicoId }
    const [dovesiFirma, setDoveSiFirma] = useState(null);
    const [modifica, setModifica] = useState(false);
    const [form, setForm] = useState(null);
    const [elenchi, setElenchi] = useState({ agende: [], stanze: [] });
    /* In modifica: il confronto e' con il modulo com'era all'apertura
       della modifica (versione sale li'); chiusura da sfondo, X, Chiudi
       e «Annulla» passano da chiedi(). */
    const [versioneForm, setVersioneForm] = useState(0);
    const sporco = useSporco(modifica ? form : null, versioneForm);
    const chiedi = useUscitaProtetta(sporco);
    const chiudi = () => { if (chiedi()) onChiudi(); };
    const annullaModifica = () => { if (chiedi()) setModifica(false); };
    const {
        ricerca: ricercaPrest, setRicerca: setRicercaPrest,
        risultati: catalogo, tutte: tuttePrest, setTutte: setTuttePrest,
        pulisci: pulisciRicerca,
    } = useRicercaPrestazioni({
        agendaId: form?.agenda_id || x.agenda_id,
        attiva: !!modifica,
    });

    /* `datetime-local` vuole l'orario da orologio a muro, senza fuso:
       quello della CLINICA, non quello del browser. Prima si componeva
       con getFullYear/getHours, cioe' col fuso della postazione. */
    const perCampo = (iso) => perCampoItaliano(iso);

    const apriModifica = async () => {
        const durata = Math.round(
            (new Date(x.fine) - new Date(x.inizio)) / 60000
        );
        setForm({
            inizio: perCampo(x.inizio),
            durata,
            agenda_id: x.agenda_id,
            stanza_id: x.stanza_id || '',
            note: x.note || '',
            prestazioni: (x.prestazioni || []).map((p) => ({
                id: p.prestazione_id || null,
                nome: p.descrizione,
                durata_minuti: p.durata,
                prezzo: p.prezzo,
            })),
        });
        setModifica(true);
        setVersioneForm((v) => v + 1);
        try {
            const [a, s] = await Promise.all([
                api.get('/agenda/agende'),
                api.get('/agenda/stanze'),
            ]);
            setElenchi({
                agende: a.data.data.agende || [],
                stanze: s.data.data.stanze || [],
            });
        } catch (e) {
            setElenchi({ agende: [], stanze: [] });
        }
    };

    const salvaModifica = () => azione(async () => {
        /* L'orario nel campo e' ora italiana: l'istante lo ricava
           `istanteItaliano`, non `new Date` col fuso del browser. */
        const inizio = istanteItaliano(form.inizio);
        /* Se ci sono prestazioni la durata la dettano loro, come in
           creazione: il campo a mano vale solo senza prestazioni. */
        const minuti = (form.prestazioni || []).length
            ? form.prestazioni.reduce((s, p) => s + (p.durata_minuti || 30), 0)
            : (parseInt(form.durata, 10) || 30);
        const fine = new Date(inizio.getTime() + minuti * 60000);
        const r = await api.put(`/agenda/${x.id}`, {
            inizio: inizio.toISOString(),
            fine: fine.toISOString(),
            agenda_id: parseInt(form.agenda_id, 10),
            stanza_id: form.stanza_id === '' ? null : parseInt(form.stanza_id, 10),
            note: form.note,
            prestazioni: (form.prestazioni || []).map((p) => ({
                prestazione_id: p.id,
                descrizione: p.nome,
                durata_minuti: p.durata_minuti,
                prezzo: p.prezzo,
            })),
        });
        const c = r.data.data?.conflitti;
        if (c && (c.stanza?.length || c.agenda?.length)) {
            notificaAvviso(r.data.message);
        }
    });

    /* `chiudiDopo` esiste perche' segnare «Arrivato» sembrava non fare
       niente: la presenza veniva salvata, ma il riquadro restava aperto e
       sopra c'era lo stesso identico pulsante. Chi lo premeva non aveva
       nessun modo di sapere che era andata. */
    const azione = async (fn, chiudiDopo = false) => {
        try {
            setInCorso(true);
            await fn();
            onAggiornato();
            /* Anche da qui: `onAggiornato` ricarica la griglia di CHI ha
               aperto il riquadro, ma un'altra scheda aperta sulla stessa
               agenda - la reception ne tiene due - resterebbe indietro. */
            annunciaAgendaCambiata();
            if (chiudiDopo) onChiudi();
        } catch (e) { notificaErrore(messaggioDa(e, 'Non riesco ad aggiornare l\u2019appuntamento')); }
        finally { setInCorso(false); }
    };

    /* Dopo la trasformazione serve il posto dove si lavora sulla visita,
       cioe' le procedure mediche: aprire la home della scheda paziente
       obbligherebbe a un altro clic per arrivarci. */
    const vaiAlleProcedure = (pazienteId) =>
        navigate(`/pazienti/${pazienteId}`, { state: { schermata: 'procedure' } });

    /* Con una visita del medico ancora aperta l'appuntamento si lega a
       quella e le prestazioni prenotate non si copiano (Alex, 3/10/2026:
       le registra il medico). Lo dice il server, e qui si mostra: chi
       trasforma deve sapere che nella visita non le trovera'. */
    const trasforma = async (documenti = []) => {
        const r = await api.post(`/agenda/${x.id}/trasforma-in-visita`,
                                 { consensi_documenti: documenti });
        if (r.data?.data?.legata) notificaAvviso(r.data.message);
        const pid = r.data?.data?.paziente_id;
        /* La voce di un pacchetto aperto non entra nella visita (Alex,
           3/10/2026): il server lo dice nel messaggio, «Pacchetto attivo:
           seduta N di M — ...», e qui si mostra. La regola sta nel database
           (pacchetti_aperti), non qui. */
        if (r.data?.data?.pacchetti?.length) notificaAvviso(r.data.message);
        if (pid) vaiAlleProcedure(pid);
    };

    /* Prima di trasformare si chiede al server che cosa manca: paziente,
       prestazioni ed esecutore li ricava lui dall'appuntamento, con le
       stesse regole della trasformazione. */
    /* Non passa da azione(): azione ricarica la griglia a lavoro finito, e
       la griglia ricaricata chiude questo riquadro - con la firma, che vive
       qui dentro. azione() serve solo alla trasformazione vera. */
    const trasformaInVisita = async () => {
        let v;
        try {
            setInCorso(true);
            v = (await templateAPI.verificaConsensi({ appuntamento_id: x.id })).data.data;
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a verificare i consensi dell\u2019appuntamento'));
            return;
        } finally { setInCorso(false); }
        const senza = v.prestazioni.find((p) => p.senza_modello);
        if (senza) { notificaErrore(senza.messaggio); return; }
        if (v.modelli.length) {
            setDoveSiFirma(null);
            setFirmaPrima({
                medicoId: v.medico_id,
                consensi: v.modelli.map((t) => {
                    const chi = v.prestazioni.find((p) => p.mancanti.includes(t.id));
                    const riga = chi && (x.prestazioni || []).find(
                        (p) => Number(p.prestazione_id) === Number(chi.prestazione_id));
                    return { ...t, _prestazione: riga ? riga.descrizione : null };
                }),
            });
            return;
        }
        azione(() => trasforma());
    };

    const firmatiPrima = (documenti) => {
        setFirmaPrima(null);
        setDoveSiFirma(null);
        const ids = (documenti || []).map((d) => d?.id ?? d?.documento_id).filter(Boolean);
        azione(() => trasforma(ids));
    };

    return (
        <>
        {/* Fuori dallo sfondo del riquadro: un clic dentro la firma non deve
            arrivare al «chiudi» dello sfondo. */}
        {firmaPrima && dovesiFirma === null && (
            <SceltaFirma
                pazienteId={x.paziente_id}
                visitaId={null}
                medicoId={firmaPrima.medicoId}
                consensi={firmaPrima.consensi}
                onSuPc={() => setDoveSiFirma('pc')}
                onCompletato={firmatiPrima}
                onAnnulla={() => { setFirmaPrima(null); setDoveSiFirma(null); }}
            />
        )}
        {firmaPrima && dovesiFirma === 'pc' && (
            <FirmaConsenso
                pazienteId={x.paziente_id}
                visitaId={null}
                medicoId={firmaPrima.medicoId}
                consensi={firmaPrima.consensi}
                obbligatori
                onClose={() => { setFirmaPrima(null); setDoveSiFirma(null); }}
                onComplete={firmatiPrima}
            />
        )}
        {/* Mentre si firma il riquadro si toglie di mezzo: la finestra di
            firma sta sotto di lui (z-index 1000 contro 1250). Annullando la
            firma torna com'era. */}
        {!firmaPrima && (
        <div className="ag-modale-sfondo" onClick={chiudi}>
            <div className="ag-modale" onClick={(e) => e.stopPropagation()}>
                <div className="ag-modale-testata">
                    <span>{orario(x.inizio)} – {orario(x.fine)}</span>
                    <span className="ag-testata-azioni">
                        {!modifica && x.stato !== 'annullato' && (
                            <button onClick={apriModifica} title="Modifica">
                                <Pencil size={16} />
                            </button>
                        )}
                        <button onClick={chiudi}><X size={18} /></button>
                    </span>
                </div>
                <div className="ag-modale-corpo">
                    <h3>
                        {x.tipo === 'blocco'
                            ? x.titolo
                            : (x.paz_cognome
                                ? `${x.paz_cognome} ${x.paz_nome}`
                                : x.paziente_nome)}
                    </h3>

                    {x.id_paziente && <div className="ag-riga">{x.id_paziente}</div>}
                    {(x.paz_telefono || x.paziente_telefono) && (
                        <div className="ag-riga">{x.paz_telefono || x.paziente_telefono}</div>
                    )}
                    {x.stanza_nome && (
                        <div className="ag-riga">
                            <span className="ag-pallino"
                                  style={{ background: x.stanza_colore }} />
                            {x.stanza_nome}
                        </div>
                    )}

                    {(x.prestazioni || []).length > 0 && (
                        <div className="ag-prestazioni">
                            {x.prestazioni.map((p, i) => (
                                <div key={i}>
                                    <span>{p.descrizione}</span>
                                    <em>{p.durata} min{p.prezzo ? ` · ${p.prezzo} €` : ''}</em>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* La seduta di un pacchetto aperto (Alex, 6/10/2026,
                        domanda 11): l'avviso di «Trasforma in visita» anche
                        qui, e per l'amministrazione, su un giorno passato,
                        «Continua pacchetto». Lo decide il server. */}
                    {!modifica && <SedutaPacchetto appuntamento={x} />}

                    {x.note && !modifica && <p className="ag-note">{x.note}</p>}

                    {modifica && form && (
                        <div className="ag-modifica">
                            <div className="ag-mod-doppio">
                                <label>
                                    <span>Inizio</span>
                                    <input
                                        type="datetime-local"
                                        step="900"
                                        value={form.inizio}
                                        onChange={(e) => setForm({ ...form, inizio: e.target.value })}
                                    />
                                </label>
                                <label className="ag-mod-corto">
                                    <span>Durata</span>
                                    <input
                                        type="number"
                                        min="5"
                                        step="5"
                                        value={(form.prestazioni || []).length
                                            ? form.prestazioni.reduce((s, p) => s + (p.durata_minuti || 30), 0)
                                            : form.durata}
                                        disabled={(form.prestazioni || []).length > 0}
                                        onChange={(e) => setForm({ ...form, durata: e.target.value })}
                                    />
                                </label>
                            </div>

                            <label>
                                <span>Agenda</span>
                                <select
                                    value={form.agenda_id}
                                    onChange={(e) => setForm({ ...form, agenda_id: e.target.value })}
                                >
                                    {elenchi.agende.map((a) => (
                                        <option key={a.id} value={a.id}>{a.nome}</option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Stanza</span>
                                <select
                                    value={form.stanza_id}
                                    onChange={(e) => setForm({ ...form, stanza_id: e.target.value })}
                                >
                                    <option value="">nessuna</option>
                                    {elenchi.stanze.map((s) => (
                                        <option key={s.id} value={s.id}>{s.nome}</option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Note</span>
                                <input
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                />
                            </label>

                            <div className="ag-mod-prestazioni">
                                <span className="ag-mod-titolo">Prestazioni</span>

                                {(form.prestazioni || []).map((p, i) => (
                                    <div key={i} className="ag-prest-scelta">
                                        <span>{p.nome}</span>
                                        <em>{p.durata_minuti || 30} min</em>
                                        <button onClick={() => setForm({
                                            ...form,
                                            prestazioni: form.prestazioni.filter((_, j) => j !== i),
                                        })}>
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}

                                <input
                                    value={ricercaPrest}
                                    onChange={(e) => setRicercaPrest(e.target.value)}
                                    placeholder="aggiungi: scrivi almeno tre lettere…"
                                />

                                <label className="ag-spunta">
                                    <input
                                        type="checkbox"
                                        checked={tuttePrest}
                                        onChange={(e) => setTuttePrest(e.target.checked)}
                                    />
                                    <span>Cerca fra tutte, non solo le sue</span>
                                </label>

                                {catalogo.length > 0 && (
                                    <div className="ag-risultati">
                                        {catalogo.map((p) => (
                                            <button key={p.id} onClick={() => {
                                                setForm({
                                                    ...form,
                                                    prestazioni: [...(form.prestazioni || []), p],
                                                });
                                                pulisciRicerca();
                                            }}>
                                                <b>{p.nome}</b>
                                                <span>
                                                    {p.durata_minuti || 30} min
                                                    {p.prezzo ? ` · ${p.prezzo} €` : ''}
                                                    {p.stanza ? ` · ${p.stanza}` : ''}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="ag-mod-azioni">
                                <button onClick={annullaModifica} disabled={inCorso}>
                                    Annulla
                                </button>
                                <button className="primario" onClick={salvaModifica} disabled={inCorso}>
                                    Salva
                                </button>
                            </div>
                        </div>
                    )}

                    {x.tipo === 'visita' && (
                        <div className="ag-presenza">
                            {/* Se «Arrivato» lega l'appuntamento a una visita
                                del medico che c'e' gia' (ancora aperta), le
                                prestazioni prenotate non ci entrano: le
                                aggiunge il medico. Lo dice il server, come per
                                «Trasforma in visita», e qui si mostra
                                (revisione del 3/10/2026 pomeriggio). */}
                            <button
                                className={x.presenza === 'presente' ? 'attivo' : ''}
                                disabled={inCorso}
                                onClick={() => azione(async () => {
                                    const r = await api.post(`/agenda/${x.id}/presenza`, { presenza: 'presente' });
                                    if (r.data?.data?.legata && r.data?.data?.prenotate) notificaAvviso(r.data.message);
                                }, true)}
                            >
                                Arrivato
                            </button>
                            <button
                                className={x.presenza === 'assente' ? 'attivo assente' : ''}
                                disabled={inCorso}
                                onClick={() => azione(() =>
                                    api.post(`/agenda/${x.id}/presenza`, { presenza: 'assente' }), true)}
                            >
                                Non presentato
                            </button>
                        </div>
                    )}

                    {/* Trasforma in visita: esplicito, non legato alla
                        presenza, perche' i conteggi vengono dalle prestazioni
                        eseguite e una visita si crea solo quando c'e' davvero.
                        Se l'appuntamento e' gia' una visita, si va lì. */}
                    {x.tipo === 'visita' && x.paziente_id && (
                        <div className="ag-trasforma-riga">
                            {x.visita_id ? (
                                <button
                                    className="ag-vai-visita"
                                    onClick={() => vaiAlleProcedure(x.paziente_id)}
                                >
                                    Vai alla visita
                                </button>
                            ) : (
                                <button
                                    disabled={inCorso}
                                    onClick={trasformaInVisita}
                                >
                                    Trasforma in visita
                                </button>
                            )}
                        </div>
                    )}

                    <div className="ag-modale-azioni">
                        <button onClick={chiudi}>Chiudi</button>
                        <button className="ag-annulla" disabled={inCorso}
                                onClick={() => {
                                    const m = window.prompt('Motivo dell\'annullamento (facoltativo)');
                                    if (m === null) return;
                                    azione(() => api.delete(`/agenda/${x.id}`, { data: { motivo: m } }));
                                }}>
                            Annulla appuntamento
                        </button>
                    </div>
                </div>
            </div>
        </div>
        )}
        </>
    );
};

/* ============================================================
   Nuovo appuntamento
   ============================================================ */
const NuovoAppuntamento = ({ contesto, onChiudi, onCreato }) => {
    const [tipo, setTipo] = useState('visita');
    const [ricerca, setRicerca] = useState('');
    const [pazienti, setPazienti] = useState([]);
    const [paziente, setPaziente] = useState(null);
    const [nomeLibero, setNomeLibero] = useState('');
    const [telefono, setTelefono] = useState('');
    const [titolo, setTitolo] = useState('');
    const [prestazioni, setPrestazioni] = useState([]);
    const {
        ricerca: ricercaPrest, setRicerca: setRicercaPrest,
        risultati: catalogo, tutte: tuttePrest, setTutte: setTuttePrest,
        pulisci: pulisciRicerca,
    } = useRicercaPrestazioni({ agendaId: contesto.agenda_id });
    const [durata, setDurata] = useState(30);
    const [note, setNote] = useState('');
    const [inCorso, setInCorso] = useState(false);

    /* Chiudere con qualcosa di diverso da com'era chiede conferma: sfondo,
       X e «Annulla» passano da chiudi(). L'istantanea e' quella del
       montaggio (il modulo nasce vuoto). */
    const sporco = useSporco({ tipo, paziente: paziente?.id || null, nomeLibero, telefono, titolo, prestazioni, durata, note }, 0);
    const chiedi = useUscitaProtetta(sporco);
    const chiudi = () => { if (chiedi()) onChiudi(); };

    useEffect(() => {
        if (ricerca.trim().length < 3) { setPazienti([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get('/pazienti', { params: { search: ricerca, limit: 8 } });
                setPazienti(r.data.data.pazienti || []);
            } catch (e) { /* niente */ }
        }, 350);
        return () => clearTimeout(t);
    }, [ricerca]);

    const durataTotale = prestazioni.length
        ? prestazioni.reduce((s, p) => s + (p.durata_minuti || 30), 0)
        : durata;

    const salva = async () => {
        try {
            setInCorso(true);
            const r = await api.post('/agenda', {
                agenda_id: contesto.agenda_id,
                /* IL CLIC SULLA GRIGLIA (corretto il 23 settembre 2026).
                   Qui partiva `"2026-09-17T10:00"`, un orario SENZA
                   fuso: il server, che gira in UTC, lo leggeva come le
                   10:00 UTC e l'appuntamento compariva alle 12:00.
                   Modifica e trascinamento convertivano gia', quindi
                   nella stessa colonna convivevano due convenzioni.
                   Misurato in produzione: tutti e sette gli
                   appuntamenti creati da una persona erano spostati. */
                inizio: istanteItaliano(contesto.inizio).toISOString(),
                tipo,
                paziente_id: paziente?.id || null,
                paziente_nome: paziente ? null : (nomeLibero || null),
                paziente_telefono: telefono || null,
                titolo: tipo === 'blocco' ? titolo : null,
                note: note || null,
                durata_minuti: durataTotale,
                prestazioni: prestazioni.map((p) => ({
                    prestazione_id: p.id,
                    descrizione: p.nome,
                    durata_minuti: p.durata_minuti,
                    prezzo: p.prezzo,
                })),
            });
            if (r.data.message && r.data.message.includes('Attenzione')) {
                notificaAvviso(r.data.message);
            }
            onCreato();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a prenotare l\u2019appuntamento'));
        } finally {
            setInCorso(false);
        }
    };

    const valido = tipo === 'blocco'
        ? titolo.trim().length > 0
        : (paziente || nomeLibero.trim().length > 0);

    return (
        <div className="ag-modale-sfondo" onClick={chiudi}>
            <div className="ag-modale" onClick={(e) => e.stopPropagation()}>
                <div className="ag-modale-testata">
                    <span>
                        <Plus size={16} /> {contesto.agenda_nome} ·{' '}
                        {contesto.inizio.slice(11)}
                    </span>
                    <button onClick={chiudi}><X size={18} /></button>
                </div>

                <div className="ag-modale-corpo">
                    <div className="ag-tipi">
                        <button className={tipo === 'visita' ? 'attivo' : ''}
                                onClick={() => setTipo('visita')}>Appuntamento</button>
                        <button className={tipo === 'blocco' ? 'attivo' : ''}
                                onClick={() => setTipo('blocco')}>Blocco</button>
                    </div>

                    {tipo === 'blocco' ? (
                        <label className="ag-campo">
                            <span>Motivo</span>
                            <input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                                   placeholder="pranzo, riunione, assenza…" autoFocus />
                        </label>
                    ) : (
                        <>
                            {paziente ? (
                                <>
                                    <div className="ag-scelto">
                                        <b>{paziente.cognome} {paziente.nome}</b>
                                        <span>{paziente.id_paziente}</span>
                                        <button onClick={() => setPaziente(null)}><X size={14} /></button>
                                    </div>
                                    {/* L'avviso sta QUI e non nella scheda paziente:
                                        chi prenota lo deve vedere prima di dare
                                        l'orario, non dopo. Non blocca niente. */}
                                    <SegnoMancate paziente={paziente} />
                                </>
                            ) : (
                                <>
                                    <label className="ag-campo">
                                        <span>Paziente</span>
                                        <input value={ricerca} autoFocus
                                               onChange={(e) => setRicerca(e.target.value)}
                                               placeholder="cognome, nome o codice fiscale…" />
                                    </label>
                                    {pazienti.length > 0 && (
                                        <div className="ag-risultati">
                                            {pazienti.map((p) => (
                                                <button key={p.id} onClick={() => {
                                                    setPaziente(p);
                                                    setTelefono(p.telefono || '');
                                                    setPazienti([]);
                                                }}>
                                                    <b>
                                                        {p.cognome} {p.nome}
                                                        <PallinoMancate paziente={p} />
                                                    </b>
                                                    <span>{p.id_paziente}
                                                        {p.data_nascita
                                                            ? ` · ${new Date(p.data_nascita)
                                                                .toLocaleDateString('it-IT')}`
                                                            : ''}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <p className="ag-nota">
                                        Se prenota al telefono e non è in archivio, basta
                                        il nome: l'anagrafica si completa all'arrivo.
                                    </p>
                                    <label className="ag-campo">
                                        <span>Oppure solo nome e telefono</span>
                                        <input value={nomeLibero}
                                               onChange={(e) => setNomeLibero(e.target.value)}
                                               placeholder="Nome e cognome" />
                                    </label>
                                </>
                            )}

                            <label className="ag-campo">
                                <span>Telefono</span>
                                <input value={telefono}
                                       onChange={(e) => setTelefono(e.target.value)} />
                            </label>

                            <label className="ag-campo">
                                <span>Prestazione</span>
                                <input value={ricercaPrest}
                                       onChange={(e) => setRicercaPrest(e.target.value)}
                                       placeholder="scrivi almeno tre lettere…" />
                            </label>
                            <label className="ag-spunta">
                                <input type="checkbox" checked={tuttePrest}
                                       onChange={(e) => setTuttePrest(e.target.checked)} />
                                <span>Cerca fra tutte le prestazioni, non solo le sue</span>
                            </label>
                            {catalogo.length > 0 && (
                                <div className="ag-risultati">
                                    {catalogo.map((p) => (
                                        <button key={p.id} onClick={() => {
                                            setPrestazioni([...prestazioni, p]);
                                            pulisciRicerca();
                                        }}>
                                            <b>{p.nome}</b>
                                            <span>
                                                {p.durata_minuti || 30} min
                                                {p.prezzo ? ` · ${p.prezzo} €` : ''}
                                                {p.stanza ? ` · ${p.stanza}` : ''}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {prestazioni.map((p, i) => (
                                <div key={i} className="ag-prest-scelta">
                                    <span>{p.nome}</span>
                                    <em>{p.durata_minuti || 30} min</em>
                                    <button onClick={() => setPrestazioni(
                                        prestazioni.filter((_, j) => j !== i))}>
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </>
                    )}

                    <label className="ag-campo ag-campo--stretto">
                        <span>Durata (minuti)</span>
                        <input type="number" step={15} min={15}
                               value={durataTotale}
                               disabled={prestazioni.length > 0}
                               onChange={(e) => setDurata(parseInt(e.target.value, 10) || 30)} />
                    </label>

                    <label className="ag-campo">
                        <span>Note</span>
                        <textarea rows={2} value={note}
                                  onChange={(e) => setNote(e.target.value)} />
                    </label>

                    <div className="ag-modale-azioni">
                        <button onClick={chiudi}>Annulla</button>
                        <button className="ag-conferma" disabled={inCorso || !valido}
                                onClick={salva}>
                            Prenota
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Agenda;
